import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { DailyChallengeSessionStatus } from '../models/cache/daily-challenge-cache-entry';
import { GameMode } from '../models/requests/game-mode.enum';
import { QuemSouEuSessionResponse } from '../models/responses/quem-sou-eu-session-response';
import { SessionStatus } from '../models/responses/session-status.enum';
import { DailyChallengeCacheService } from './daily-challenge-cache';
import { QuemSouEuApiService } from './requests/quem-sou-eu';

/** Status finais que atualizam o cache do Desafio Diário. */
const FINAL_CACHE_STATUS: Partial<Record<SessionStatus, DailyChallengeSessionStatus>> = {
  [SessionStatus.Vitoria]: 'VITORIA',
  [SessionStatus.Derrota]: 'DERROTA',
};

@Injectable({ providedIn: 'root' })
export class GameSessionService {
  private readonly quemSouEuApi = inject(QuemSouEuApiService);
  private readonly dailyCache = inject(DailyChallengeCacheService);

  private readonly currentSnapshot = signal<QuemSouEuSessionResponse | null>(null);
  /**
   * Estado da sessão no momento em que foi aberta ou recarregada (histórico + contadores
   * + limites), usado pela tela de jogo para se montar. Toda rota de criação já devolve
   * a sessão completa, inclusive no Modo Solo.
   */
  readonly snapshot = this.currentSnapshot.asReadonly();
  readonly idSession = computed(() => this.currentSnapshot()?.idSession ?? null);
  readonly mode = computed(() => this.currentSnapshot()?.idGameMode ?? GameMode.ModoSolo);

  /** Cria uma partida do Modo Solo (`POST /quem-sou-eu/sessions`) e a ativa. */
  async start(idTheme: string): Promise<QuemSouEuSessionResponse> {
    const session = await firstValueFrom(this.quemSouEuApi.startSession({ idTheme }));
    this.load(session);
    return session;
  }

  /** Ativa uma sessão já criada no backend (partida nova ou retomada do Desafio Diário). */
  load(session: QuemSouEuSessionResponse): void {
    this.currentSnapshot.set(session);
  }

  /**
   * Busca de novo o estado da sessão ativa (ex.: após um 409). Só existe para o Desafio
   * Diário: a API ainda não tem rota de recarga para o Modo Solo.
   */
  async reload(): Promise<QuemSouEuSessionResponse> {
    const idSession = this.idSession();
    if (!idSession || this.mode() !== GameMode.DailyDash) {
      throw new Error('Só é possível recarregar uma sessão do Desafio Diário.');
    }
    const session = await firstValueFrom(this.quemSouEuApi.getSession(idSession));
    this.load(session);
    return session;
  }

  /**
   * Registra o status da sessão ativa após uma interação ou desistência. No Desafio
   * Diário, vitória/derrota ficam gravadas no cache (o dia não pode ser jogado de novo).
   */
  registerStatus(status: SessionStatus): void {
    const session = this.currentSnapshot();
    const cacheStatus = FINAL_CACHE_STATUS[status];
    if (!session?.challengeDate || !cacheStatus) {
      return;
    }
    this.dailyCache.save(session.idSession, session.theme.id, session.challengeDate, cacheStatus);
  }
}
